import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import CouponsList from 'components/Coupons/CouponsList';
import ErrorBoundary from 'components/ErrorBoundary';
import GenericError from 'components/GenericError';

import placeholder from 'lib/placeholder';
import { couponQueryKeys } from 'lib/queryKeys';

import { getCouponsList } from 'services/coupon';

import Schema from './Schema';

import COUPONS from 'constants/coupons';
import { SORT_VALUES } from 'constants/sort';
import STORES from 'constants/store';

const ExpiredCouponsSection = dynamic(
  () => import('components/Coupons/ExpiredCouponsSection')
);

const MAX_EXPIRED_COUPONS = 5;

const StoreCouponsList = ({
  gaEventCategory,
  serverCoupons,
  storeId,
  storeName,
  storeSlug = null,
}) => {
  const { activeCoupons, finishedCoupons } = useMemo(() => {
    const active = [];
    const finished = [];

    for (const coupon of serverCoupons.coupons) {
      const { couponStatusName } = coupon;

      if (couponStatusName === COUPONS.STATUS.FINISHED) {
        finished.push(coupon);
      }

      if (couponStatusName === COUPONS.STATUS.APPROVED) {
        active.push(coupon);
      }
    }

    return {
      activeCoupons: active,
      finishedCoupons: finished.slice(0, MAX_EXPIRED_COUPONS),
    };
  }, [serverCoupons]);

  /**
   * Promobit coupon page uses paginated mode (infinite scroll with
   * getCouponsList). Store pages use static mode — SSR delivers the
   * final coupon list, no client-side pagination needed.
   */
  const isPromobit = storeId === STORES.PROMOBIT_STORE_ID;

  const promobitProps = useMemo(() => {
    if (!isPromobit) return null;

    return {
      initialCoupons: serverCoupons,
      queryFn: ({ page = 1 }, opts) =>
        getCouponsList(
          {
            after: page,
            limit: COUPONS.COUPONS_LIMIT,
            sort: SORT_VALUES.LATEST,
            couponCodeFilter: COUPONS.PROMOBIT_COUPON_CODE,
          },
          opts
        ),
      queryKey: couponQueryKeys.listByCouponCode({
        couponCode: COUPONS.PROMOBIT_COUPON_CODE,
        sortType: SORT_VALUES.LATEST,
      }),
      withCouponStoreCover: true,
    };
  }, [isPromobit, serverCoupons]);

  return (
    <>
      <ErrorBoundary
        fallback={
          <GenericError
            message={placeholder('errors.couldNotLoadCouponsList')}
          />
        }
      >
        <Schema coupons={isPromobit ? serverCoupons.coupons : activeCoupons} />
        {isPromobit ? (
          <CouponsList gaEventCategory={gaEventCategory} {...promobitProps} />
        ) : (
          <CouponsList
            coupons={activeCoupons}
            gaEventCategory={gaEventCategory}
            storeId={storeId}
            storeName={storeName}
            storeSlug={storeSlug}
          />
        )}
      </ErrorBoundary>
      {finishedCoupons.length > 0 && !isPromobit && (
        <ExpiredCouponsSection
          className="mt-12 lg:mt-16"
          coupons={finishedCoupons}
          gaEventCategory={gaEventCategory}
          storeName={storeName}
          storeSlug={storeSlug}
        />
      )}
    </>
  );
};

StoreCouponsList.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  serverCoupons: PropTypes.shape({
    after: PropTypes.string,
    coupons: PropTypes.arrayOf(
      PropTypes.shape({
        couponId: PropTypes.number.isRequired,
        couponDiscountValue: PropTypes.string.isRequired,
        couponDiscountShort: PropTypes.string.isRequired,
        couponDiscount: PropTypes.string.isRequired,
        couponDiscountOn: PropTypes.string.isRequired,
        couponStatusName: PropTypes.string.isRequired,
        couponInstructions: PropTypes.string.isRequired,
        couponVerified: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        storeImage: PropTypes.string,
        storeName: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeSlug: PropTypes.string.isRequired,
        couponTag: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        couponPublished: PropTypes.string.isRequired,
        couponTitle: PropTypes.string.isRequired,
        couponCode: PropTypes.string.isRequired,
        couponUrl: PropTypes.string.isRequired,
        userName: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  storeId: PropTypes.number.isRequired,
  storeImage: PropTypes.string,
  storeName: PropTypes.string.isRequired,
  storeSlug: PropTypes.string,
};

export default StoreCouponsList;
